/* eslint-disable jira/filename-case */
/**
 * This library is the only place where you'd use raw analytics from atlaskit
 */

import last from 'lodash/last';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import {
	type EventTypes,
	OPERATIONAL_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	UI_EVENT_TYPE,
} from '../constants.tsx';
import type { AnalyticsAttributesUntyped, AnalyticsEvent } from '../types.tsx';
import {
	type RestArguments,
	type AnalyticsEventOverrides,
	getSubjectsFromArguments,
	type ActionSubjectAndAction,
} from './extract-data-from-event.tsx';

export const fireAnalytics = (
	event: AnalyticsEvent,
	type: EventTypes,
	eventOverrides:
		| AnalyticsEventOverrides
		| {
				attributes: AnalyticsEventOverrides['attributes'];
		  }
		| {} = {},
): void => {
	// Safe check to prevent SSR to trigger and to avoid sentry errors such as https://ops.internal.atlassian.com/jira/browse/HOT-90825
	if (__SERVER__) return;
	const clonedEvent = event.clone();
	if (clonedEvent) {
		clonedEvent
			.update({
				...eventOverrides,
				analyticsType: type,
			})
			.fire('productAnalytics');
	}
};
const getActionSubjectFromContext = (event: AnalyticsEvent) =>
	last(event.context.map((i) => i.component || i.componentName).filter(Boolean));
const fireEventWhenIdle = <T extends Array<unknown>>(
	fireFn: (event: AnalyticsEvent, ...rest: T) => void,
	event: AnalyticsEvent,
	...rest: T
) => {
	const browserWindow = getWindow();
	if (typeof browserWindow?.requestIdleCallback === 'function') {
		browserWindow.requestIdleCallback(() => fireFn(event, ...rest));
	} else {
		fireFn(event, ...rest);
	}
};
/**
 * Triggers analytics for user interface interactions with consolidated event data.
 */
export const fireUIAnalytics = (event: AnalyticsEvent, ...rest: RestArguments): void => {
	const {
		action: attrAction,
		actionSubject: attrSubject,
		actionSubjectId,
		attributes,
	} = getSubjectsFromArguments(...rest);
	const action = event.payload?.action || attrAction;
	const actionSubject =
		event.payload?.actionSubject || attrSubject || getActionSubjectFromContext(event);
	fireAnalyticsOfType(event, UI_EVENT_TYPE, {
		action,
		actionSubject,
		actionSubjectId,
		attributes,
	});
};
const capitalize = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
const fireAnalyticsOfType = (
	event: AnalyticsEvent,
	eventType: EventTypes,
	analyticsProps: AnalyticsEventOverrides,
) => {
	// UI_EVENT_TYPE condition - just not to affect existing events we're sending; not to require update of existing monitoring
	const eventTypeForError =
		eventType === UI_EVENT_TYPE ? eventType : capitalize(eventType.toLowerCase());
	if (!event.clone) {
		throw Error(`There is no analytics event from atlaskit in ${eventTypeForError} event`);
	}
	if (!analyticsProps.action) {
		throw Error(`Mandatory "action" field is missing from ${eventTypeForError} event`);
	}
	if (!analyticsProps.actionSubject) {
		throw Error(`Mandatory "actionSubject" field is missing from ${eventTypeForError} event`);
	}
	fireAnalytics(event, eventType, analyticsProps);
};
export const fireTrackAnalytics = (event: AnalyticsEvent, ...rest: RestArguments): void => {
	const analyticsProps = getSubjectsFromArguments(...rest);
	fireAnalyticsOfType(event, TRACK_EVENT_TYPE, analyticsProps);
};
export const fireTrackAnalyticsDeferred = (event: AnalyticsEvent, ...rest: RestArguments): void =>
	fireEventWhenIdle(fireTrackAnalytics, event, ...rest);
export const fireOperationalAnalytics = (event: AnalyticsEvent, ...rest: RestArguments): void => {
	const analyticsProps = getSubjectsFromArguments(...rest);
	fireAnalyticsOfType(event, OPERATIONAL_EVENT_TYPE, analyticsProps);
};
export const fireOperationalAnalyticsDeferred = (
	event: AnalyticsEvent,
	...rest: RestArguments
): void => fireEventWhenIdle(fireOperationalAnalytics, event, ...rest);
export const fireScreenAnalytics = (
	event: AnalyticsEvent,
	attributes: AnalyticsAttributesUntyped = {},
): void => {
	if (!event.clone) {
		throw Error('There is no analytics event from atlaskit in Screen event');
	}
	fireAnalytics(event, SCREEN_EVENT_TYPE, {
		attributes,
	});
};
export const fireScreenAnalyticsDeferred = (
	event: AnalyticsEvent,
	attributes: AnalyticsAttributesUntyped = {},
): void => fireEventWhenIdle(fireScreenAnalytics, event, attributes);
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { RestArguments, ActionSubjectAndAction };
